import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Space, Alert, Typography, Form } from 'antd';
import {
    KeyOutlined,
    CopyOutlined,
    DownloadOutlined,
    PrinterOutlined,
    ReloadOutlined,
    LockOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
    getBackupCodes,
    regenerateBackupCodes,
    selectBackupCodes,
    selectUsedCodes,
    selectMFALoading,
    selectMFAError
} from '../../redux/slices/mfaSlice';

const { Text, Paragraph } = Typography;

const BackupCodesModal = ({ visible, onClose }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // Redux state
    const backupCodes = useSelector(selectBackupCodes);
    const usedCodes = useSelector(selectUsedCodes);
    const loading = useSelector(selectMFALoading);
    const error = useSelector(selectMFAError);

    // Local state
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(true);
    const [showRegeneratePrompt, setShowRegeneratePrompt] = useState(false);
    const [authenticating, setAuthenticating] = useState(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!visible) {
            setShowPasswordPrompt(true);
            setShowRegeneratePrompt(false);
            form.resetFields();
        }
    }, [visible, form]);

    const handleAuthenticate = async () => {
        try {
            const values = await form.validateFields();
            setAuthenticating(true);

            const result = await dispatch(getBackupCodes(values.password));

            if (result.type.endsWith('/fulfilled')) {
                setShowPasswordPrompt(false);
                form.resetFields();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setAuthenticating(false);
        }
    };

    const handleOpenRegeneratePrompt = () => {
        setShowRegeneratePrompt(true);
        form.resetFields();
    };

    const handleCloseRegeneratePrompt = () => {
        setShowRegeneratePrompt(false);
        form.resetFields();
    };

    const handleRegenerate = async () => {
        try {
            const values = await form.validateFields();
            setAuthenticating(true);

            const result = await dispatch(regenerateBackupCodes(values.password));

            if (result.type.endsWith('/fulfilled')) {
                setShowRegeneratePrompt(false);
                form.resetFields();
            }
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setAuthenticating(false);
        }
    };

    const handleCopyBackupCodes = () => {
        if (backupCodes && backupCodes.length > 0) {
            const codesText = backupCodes.join('\n');
            navigator.clipboard.writeText(codesText);
            toast.success('Backup codes copied to clipboard', {
                position: 'top-right',
                duration: 2000,
            });
        }
    };

    const handleDownloadBackupCodes = () => {
        if (backupCodes && backupCodes.length > 0) {
            const codesText = `Neurula Health - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString('en-GB')}\n\n${backupCodes.map((code, index) => `${index + 1}. ${code}`).join('\n')}\n\n⚠️ IMPORTANT: Keep these codes secure!\n• Each code can only be used once\n• Use these if you lose access to your authenticator app\n• Store them in a secure location`;

            const blob = new Blob([codesText], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `neurula-mfa-backup-codes-${Date.now()}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success('Backup codes downloaded', {
                position: 'top-right',
                duration: 2000,
            });
        }
    };

    const handlePrintBackupCodes = () => {
        if (backupCodes && backupCodes.length > 0) {
            const printWindow = window.open('', '_blank');
            printWindow.document.write(`
                <html>
                <head>
                    <title>Neurula Health - MFA Backup Codes</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 40px; }
                        h1 { color: #2E5A87; }
                        .codes { margin: 20px 0; font-family: monospace; font-size: 14px; }
                        .code { margin: 8px 0; padding: 8px; background: #f5f5f5; }
                        .code.used { text-decoration: line-through; color: #999; }
                        .warning { color: #E53935; margin-top: 20px; }
                        .footer { margin-top: 40px; color: #666; font-size: 12px; }
                    </style>
                </head>
                <body>
                    <h1>Neurula Health - MFA Backup Codes</h1>
                    <p>Generated: ${new Date().toLocaleString('en-GB')}</p>
                    <div class="codes">
                        ${backupCodes.map((code, index) => {
                            const isUsed = usedCodes && usedCodes.includes(code);
                            return `<div class="code ${isUsed ? 'used' : ''}">${index + 1}. ${code} ${isUsed ? '(USED)' : ''}</div>`;
                        }).join('')}
                    </div>
                    <div class="warning">
                        <strong>⚠️ IMPORTANT:</strong>
                        <ul>
                            <li>Keep these codes secure!</li>
                            <li>Each code can only be used once</li>
                            <li>Use these if you lose access to your authenticator app</li>
                            <li>Store them in a secure location</li>
                        </ul>
                    </div>
                    <div class="footer">
                        <p>Neurula Health EMR - Confidential</p>
                    </div>
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.print();
        }
    };

    const renderPasswordPrompt = () => (
        <Space direction="vertical" size="large" style={{ width: '100%', padding: '20px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <LockOutlined style={{ fontSize: '48px', color: '#2E5A87' }} />
            </div>

            <Paragraph style={{ textAlign: 'center' }}>
                For security reasons, please enter your password to view your backup codes.
            </Paragraph>

            {error && (
                <Alert
                    message="Authentication Failed"
                    description={error}
                    type="error"
                    showIcon
                />
            )}

            <Form form={form} layout="vertical">
                <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: true, message: 'Please enter your password' }]}
                >
                    <Input.Password
                        placeholder="Enter your password"
                        size="large"
                        autoFocus
                        onPressEnter={handleAuthenticate}
                    />
                </Form.Item>
            </Form>

            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleAuthenticate}
                        loading={authenticating}
                    >
                        Continue
                    </Button>
                </Space>
            </div>
        </Space>
    );

    const renderBackupCodes = () => {
        const unusedCodesCount = backupCodes.length - (usedCodes?.length || 0);

        return (
            <Space direction="vertical" size="large" style={{ width: '100%', padding: '20px 0' }}>
                {unusedCodesCount < 3 && (
                    <Alert
                        message="Low Backup Codes"
                        description={`You have ${unusedCodesCount} unused backup codes remaining. Consider regenerating new codes.`}
                        type="warning"
                        showIcon
                    />
                )}

                <Alert
                    message="About Backup Codes"
                    description="Use these codes if you lose access to your authenticator app. Each code can only be used once."
                    type="info"
                    showIcon
                />

                <div
                    style={{
                        padding: '16px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    {backupCodes && backupCodes.map((code, index) => {
                        const isUsed = usedCodes && usedCodes.includes(code);
                        return (
                            <div
                                key={index}
                                style={{
                                    padding: '10px',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    borderBottom: index < backupCodes.length - 1 ? '1px solid #e0e0e0' : 'none',
                                    textDecoration: isUsed ? 'line-through' : 'none',
                                    color: isUsed ? '#999' : '#000',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <span>{index + 1}. {code}</span>
                                {isUsed && <Text type="secondary" style={{ fontSize: '12px' }}>(USED)</Text>}
                            </div>
                        );
                    })}
                </div>

                <Space size="middle" style={{ width: '100%', justifyContent: 'center' }}>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleDownloadBackupCodes}
                    >
                        Download
                    </Button>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyBackupCodes}
                    >
                        Copy All
                    </Button>
                    <Button
                        icon={<PrinterOutlined />}
                        onClick={handlePrintBackupCodes}
                    >
                        Print
                    </Button>
                </Space>

                <div style={{ textAlign: 'center', marginTop: '8px' }}>
                    <Button
                        type="link"
                        icon={<ReloadOutlined />}
                        onClick={handleOpenRegeneratePrompt}
                    >
                        Regenerate Backup Codes
                    </Button>
                </div>
            </Space>
        );
    };

    const renderRegeneratePrompt = () => (
        <Space direction="vertical" size="large" style={{ width: '100%', padding: '20px 0' }}>
            <Alert
                message="⚠️ Warning: This will invalidate all current backup codes"
                description="After regenerating, your old backup codes will no longer work. Make sure to save your new codes."
                type="warning"
                showIcon
            />

            <Paragraph>
                To confirm, please enter your password:
            </Paragraph>

            {error && (
                <Alert
                    message="Authentication Failed"
                    description={error}
                    type="error"
                    showIcon
                />
            )}

            <Form form={form} layout="vertical">
                <Form.Item
                    name="password"
                    label="Password"
                    rules={[{ required: true, message: 'Please enter your password' }]}
                >
                    <Input.Password
                        placeholder="Enter your password"
                        size="large"
                        autoFocus
                        onPressEnter={handleRegenerate}
                    />
                </Form.Item>
            </Form>

            <div style={{ textAlign: 'right' }}>
                <Space>
                    <Button onClick={handleCloseRegeneratePrompt}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        danger
                        onClick={handleRegenerate}
                        loading={authenticating}
                        icon={<ReloadOutlined />}
                    >
                        Regenerate Codes
                    </Button>
                </Space>
            </div>
        </Space>
    );

    return (
        <Modal
            title={
                <Space>
                    <KeyOutlined style={{ color: '#2E5A87' }} />
                    <span>Backup Codes</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={600}
            centered
            destroyOnClose
            maskClosable={false}
        >
            {showPasswordPrompt && renderPasswordPrompt()}
            {!showPasswordPrompt && !showRegeneratePrompt && renderBackupCodes()}
            {showRegeneratePrompt && renderRegeneratePrompt()}
        </Modal>
    );
};

export default BackupCodesModal;
